import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const API = `${BACKEND_URL}/api`;

export default function OutflowManager({ session, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  
  const initial = session?.outflow || {};
  const [outflow, setOutflow] = useState({
    maintenance: initial.maintenance || 0,
    gst: initial.gst || 0,
    staff_salary: initial.staff_salary || 0,
    tech: initial.tech || 0,
    miscellaneous: initial.miscellaneous || 0
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setOutflow(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/audit/session/${session.id}/outflow`, outflow);
      toast.success("Outflow data saved successfully");
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(`Failed to save: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Calculate sum of manual outflow entries (excludes commission as it comes from tool data)
  const totalEntered = Object.values(outflow).reduce((a, b) => a + (b || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-orange-600 border-orange-200 hover:bg-orange-50">
          {session.outflow ? "Edit Outflow" : "Add Outflow"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Manage Outflow</DialogTitle>
          <DialogDescription>
            Enter the expenses for <strong>{session.property_name}</strong> ({session.audit_month}).
            Commission is automatically tracked via the audit tool.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="maintenance" className="text-right text-xs font-semibold">Maintenance</Label>
            <Input id="maintenance" name="maintenance" type="number" className="col-span-3" value={outflow.maintenance || ""} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="gst" className="text-right text-xs font-semibold">GST</Label>
            <Input id="gst" name="gst" type="number" className="col-span-3" value={outflow.gst || ""} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="staff_salary" className="text-right text-xs font-semibold">Staff Salary</Label>
            <Input id="staff_salary" name="staff_salary" type="number" className="col-span-3" value={outflow.staff_salary || ""} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="tech" className="text-right text-xs font-semibold">Tech</Label>
            <Input id="tech" name="tech" type="number" className="col-span-3" value={outflow.tech || ""} onChange={handleChange} />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="miscellaneous" className="text-right text-xs font-semibold">Miscellaneous</Label>
            <Input id="miscellaneous" name="miscellaneous" type="number" className="col-span-3" value={outflow.miscellaneous || ""} onChange={handleChange} />
          </div>
          
          <div className="flex justify-between items-center bg-muted/50 p-3 rounded-md mt-2 border">
            <span className="text-sm font-semibold">Total Manual Outflow:</span>
            <span className="font-mono text-lg font-bold">₹{totalEntered.toLocaleString()}</span>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={busy} onClick={handleSave} className="w-full">
            {busy ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
